import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useUser } from '../context/UserContext';
import { authStore } from '../context/UserContext';
import { signIn, signUp, signInWithProvider } from '../authApi';
import './Auth.css';

const FADE_UP = {
  hidden:  { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
};

// ── Side panel ────────────────────────────────────────────────────────────────
function SidePanel() {
  return (
    <div className="auth-side">
      <div className="auth-side-inner">
        <Link to="/" className="auth-brand">
          <div className="nav-brand-icon" style={{ width: 40, height: 40, fontSize: 22, borderRadius: 12 }}>🌵</div>
          <span style={{ fontSize: '1.3rem', fontWeight: 800, color: '#fff' }}>Cactus Insights</span>
        </Link>
        <div className="auth-side-content">
          <h2 className="auth-side-title">Own your<br/>vehicle data.</h2>
          <p className="auth-side-sub">
            Cryptographically signed sessions. Six-tier ML pipeline.
            AI diagnostics powered by your own API key. No data selling. Ever.
          </p>
          <div className="auth-side-pills">
            {['Ed25519 Signed', 'RFC 3161 Timestamps', 'Zero Data Brokerage', 'BYOK AI'].map(p => (
              <div key={p} className="auth-side-pill">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.8)" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                {p}
              </div>
            ))}
          </div>
        </div>
        <div className="auth-side-quote">
          <p>"Your car knows more about its health than any mechanic's visual inspection. Cactus makes that data yours."</p>
          <div className="auth-side-quote-attr">— Acty Labs</div>
        </div>
      </div>
    </div>
  );
}

// ── Google SVG logo ───────────────────────────────────────────────────────────
function GoogleLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
      <path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34.1 6.8 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.7-.4-3.9z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.5 16 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34.1 6.8 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.2 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8H6.4C9.7 35.8 16.3 44 24 44z"/>
      <path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.3 4.1-4.2 5.4l6.2 5.2C37 38.2 44 33 44 24c0-1.3-.1-2.7-.4-3.9z"/>
    </svg>
  );
}

// ── Login ─────────────────────────────────────────────────────────────────────
export function LoginPage() {
  const [form, setForm]       = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [gLoading, setGLoad]  = useState(false);
  const [error, setError]     = useState('');
  const navigate              = useNavigate();

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await signIn(form.email, form.password);
      navigate('/dashboard');
    } catch (err) {
      setError(err.message || 'Sign in failed. Check your email and password.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleClick = async () => {
    setError('');
    setGLoad(true);
    try {
      await signInWithProvider('google');
    } catch (err) {
      setError(err.message || 'Google sign-in failed.');
      setGLoad(false);
    }
  };

  return (
    <div className="auth-layout">
      <SidePanel/>
      <div className="auth-form-panel">
        <motion.div className="auth-form-wrap" variants={FADE_UP} initial="hidden" animate="visible">
          <div className="auth-form-header">
            <h1 className="auth-form-title">Welcome back</h1>
            <p className="auth-form-sub">Sign in to your Cactus Insights account</p>
          </div>

          <div className="auth-providers">
            <button className="auth-provider-btn auth-provider-google" onClick={handleGoogleClick} disabled={gLoading}>
              {gLoading ? <span className="auth-spinner auth-spinner-dark"/> : <GoogleLogo/>}
              Continue with Google
            </button>
          </div>

          <div className="divider">or sign in with email</div>

          {error && <div className="auth-error">{error}</div>}
          <form onSubmit={handleEmailLogin} className="auth-form">
            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                type="email" className="form-input"
                placeholder="you@example.com"
                value={form.email}
                onChange={e => setForm({...form, email: e.target.value})}
                required
              />
            </div>
            <div className="form-group">
              <div className="auth-label-row">
                <label className="form-label">Password</label>
                <a href="#forgot" className="auth-forgot">Forgot password?</a>
              </div>
              <input
                type="password" className="form-input"
                placeholder="••••••••"
                value={form.password}
                onChange={e => setForm({...form, password: e.target.value})}
                required
              />
            </div>
            <button type="submit" className="btn btn-primary" style={{ width: '100%' }} disabled={loading}>
              {loading ? <span className="auth-spinner"/> : 'Sign In'}
            </button>
          </form>

          <p className="auth-switch">
            Don't have an account? <Link to="/register">Create one free</Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}

// ── Register ──────────────────────────────────────────────────────────────────
export function RegisterPage() {
  const [step, setStep]   = useState(1);
  const [form, setForm]   = useState({
    username: '', email: '', password: '', confirmPw: '', region: '',
    vehicleYear: '', vehicleMake: '', vehicleModel: '', vehicleTrim: '', vehicleDrivetrain: '',
  });
  const [loading, setLoading] = useState(false);
  const [gLoading, setGLoad]  = useState(false);
  const [error, setError]     = useState('');
  const navigate              = useNavigate();

  const handleGoogleClick = async () => {
    setError('');
    setGLoad(true);
    try {
      await signInWithProvider('google');
    } catch (err) {
      setError(err.message || 'Google sign-in failed.');
      setGLoad(false);
    }
  };

  const validateStep1 = () => {
    if (form.username.length < 2)  return 'Username must be at least 2 characters.';
    if (!form.email.includes('@'))  return 'Please enter a valid email.';
    if (form.password.length < 8)   return 'Password must be at least 8 characters.';
    if (form.password !== form.confirmPw) return 'Passwords do not match.';
    return null;
  };

  const handleNext = async (e) => {
    e.preventDefault();
    setError('');
    if (step === 1) {
      const err = validateStep1();
      if (err) { setError(err); return; }
    }
    if (step < 3) { setStep(step + 1); return; }
    await handleSubmit(false);
  };

  const handleSubmit = async (skipVehicle = true) => {
    setLoading(true);
    setError('');

    const vehicles = (!skipVehicle && form.vehicleMake && form.vehicleModel) ? [{
      id:          `v${Date.now()}`,
      year:        form.vehicleYear,
      make:        form.vehicleMake,
      model:       form.vehicleModel,
      trim:        form.vehicleTrim,
      drivetrain:  form.vehicleDrivetrain,
    }] : [];

    const email = form.email.toLowerCase();

    const localAccount = {
      username:        form.username,
      displayName:     form.username,
      email,
      region:          form.region,
      vehicles,
      obdAdapters:     [],
      activeVehicleId: vehicles[0]?.id || null,
    };
    authStore.createAccount(localAccount);

    try {
      const data = await signUp(email, form.password, {
        username:  form.username,
        full_name: form.username,
        region:    form.region,
      });

      if (data.session) {
        navigate('/dashboard');
      } else {
        setError('Almost there! Check your email and click the confirmation link to activate your account.');
        setLoading(false);
      }
    } catch (err) {
      authStore.deleteAccount(email);
      setError(err.message || 'Could not create account.');
      setLoading(false);
    }
  };

  const REGIONS    = ['North America', 'Europe', 'Asia Pacific', 'Latin America', 'Middle East & Africa'];
  const MAKES      = ['Toyota', 'Honda', 'Ford', 'Chevrolet', 'BMW', 'Mercedes', 'Audi', 'Volkswagen', 'Subaru', 'Mazda', 'Nissan', 'Hyundai', 'Kia', 'Dodge', 'Jeep', 'Ram', 'GMC'];
  const DRIVETRAINS= ['FWD', 'RWD', 'AWD', '4WD'];

  return (
    <div className="auth-layout">
      <SidePanel/>
      <div className="auth-form-panel">
        <motion.div className="auth-form-wrap" variants={FADE_UP} initial="hidden" animate="visible">

          <div className="auth-steps">
            {[1,2,3].map(s => (
              <div key={s} className={`auth-step ${step >= s ? 'active' : ''} ${step > s ? 'done' : ''}`}>
                <div className="auth-step-dot">{step > s ? '✓' : s}</div>
                <div className="auth-step-label">{['Account', 'Region', 'Vehicle'][s-1]}</div>
              </div>
            ))}
          </div>

          <div className="auth-form-header" style={{ marginTop: '1.5rem' }}>
            <h1 className="auth-form-title">
              {step === 1 ? 'Create your account' : step === 2 ? 'Your region' : 'Your vehicle'}
            </h1>
            <p className="auth-form-sub">
              {step === 1 ? 'Free forever. No credit card.' : step === 2 ? 'Used for anonymized fleet analytics only.' : 'You can add more vehicles later.'}
            </p>
          </div>

          {step === 1 && (
            <>
              <div className="auth-providers">
                <button className="auth-provider-btn auth-provider-google" onClick={handleGoogleClick} disabled={gLoading}>
                  {gLoading ? <span className="auth-spinner auth-spinner-dark"/> : <GoogleLogo/>}
                  Continue with Google
                </button>
              </div>
              <div className="divider">or create with email</div>
            </>
          )}

          {error && <div className="auth-error">{error}</div>}

          <form onSubmit={handleNext} className="auth-form">
            {step === 1 && (
              <>
                <div className="form-group">
                  <label className="form-label">Username</label>
                  <input type="text" className="form-input" placeholder="johndoe"
                    value={form.username} onChange={e => setForm({...form, username: e.target.value})} required minLength={2}/>
                </div>
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input type="email" className="form-input" placeholder="you@example.com"
                    value={form.email} onChange={e => setForm({...form, email: e.target.value})} required/>
                </div>
                <div className="form-group">
                  <label className="form-label">Password</label>
                  <input type="password" className="form-input" placeholder="At least 8 characters"
                    value={form.password} onChange={e => setForm({...form, password: e.target.value})} required minLength={8}/>
                </div>
                <div className="form-group">
                  <label className="form-label">Confirm Password</label>
                  <input type="password" className="form-input" placeholder="••••••••"
                    value={form.confirmPw} onChange={e => setForm({...form, confirmPw: e.target.value})} required/>
                </div>
              </>
            )}

            {step === 2 && (
              <div className="form-group">
                <label className="form-label">Region</label>
                <div className="auth-region-grid">
                  {REGIONS.map(r => (
                    <button type="button" key={r}
                      className={`auth-region-btn ${form.region === r ? 'selected' : ''}`}
                      onClick={() => setForm({...form, region: r})}
                    >{r}</button>
                  ))}
                </div>
              </div>
            )}

            {step === 3 && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Year</label>
                    <input type="number" className="form-input" placeholder="2024"
                      min="1990" max="2026" value={form.vehicleYear}
                      onChange={e => setForm({...form, vehicleYear: e.target.value})}/>
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Make</label>
                    <select className="form-input" value={form.vehicleMake}
                      onChange={e => setForm({...form, vehicleMake: e.target.value})}>
                      <option value="">Select…</option>
                      {MAKES.map(m => <option key={m}>{m}</option>)}
                    </select>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Model</label>
                    <input type="text" className="form-input" placeholder="Camry, RAV4…"
                      value={form.vehicleModel} onChange={e => setForm({...form, vehicleModel: e.target.value})}/>
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Trim <span style={{ color: '#94A3B8', fontWeight: 400 }}>(optional)</span></label>
                    <input type="text" className="form-input" placeholder="SE, XLE…"
                      value={form.vehicleTrim} onChange={e => setForm({...form, vehicleTrim: e.target.value})}/>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Drivetrain</label>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {DRIVETRAINS.map(d => (
                      <button type="button" key={d}
                        className={`auth-drivetrain-btn ${form.vehicleDrivetrain === d ? 'selected' : ''}`}
                        onClick={() => setForm({...form, vehicleDrivetrain: d})}
                      >{d}</button>
                    ))}
                  </div>
                </div>
                <p style={{ fontSize: '0.8rem', color: '#94A3B8', marginTop: '-0.5rem' }}>
                  Vehicle fields are all optional — you can add them later.
                </p>
              </>
            )}

            <div className="auth-form-actions">
              {step > 1 && (
                <button type="button" className="btn btn-ghost" onClick={() => { setError(''); setStep(step - 1); }}>
                  Back
                </button>
              )}
              <button type="submit" className="btn btn-primary" style={{ flex: 1 }}
                disabled={loading || (step === 2 && !form.region)}>
                {loading ? <span className="auth-spinner"/> : step < 3 ? 'Continue' : 'Create Account'}
              </button>
            </div>

            {step === 3 && (
              <button type="button" className="btn btn-ghost btn-sm"
                style={{ width: '100%', marginTop: '0.25rem' }}
                onClick={() => handleSubmit(true)}
                disabled={loading}>
                Skip — add vehicle later
              </button>
            )}
          </form>

          {step === 1 && (
            <p className="auth-switch">Already have an account? <Link to="/login">Sign in</Link></p>
          )}
        </motion.div>
      </div>
    </div>
  );
}
